// QR Kiosk v2 — KV2-3 create/edit report (ONE component, isNew = !id). Photo-first
// layout: photos, title*, part picker*, deselectable urgency, alarm code, description,
// ALWAYS-VISIBLE open date + time PREFILLED to now (kiosk review §4 — owner override
// of the D6 "Happened earlier?" disclosure, which hid empty fields = the empty
// mm/dd/yyyy bug), technical notes (D6/PG-13), validation summary + inline errors
// (send stays enabled), file pipeline, ?machine= context recovery. Edit-mode Write
// guard (KV2-3-g). All thunks reused unchanged.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import { changeServiceReport, createServiceReport, getMachine, getMachineParts, getServiceReport, getServiceReports } from "../../publicSlice";
import { setValidationSummary } from "../kioskV2Slice";
import { ChangeServiceReport, ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { KPage } from "../shared/KPage";
import { GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { ValidationSummary } from "../shared/ValidationSummary";
import { FormSkeleton } from "./ReportSkeletons";
import { FLabel, FieldError, KTextInput } from "./formAtoms";
import { EditableField } from "./FieldEditor";
import { KDateTimeField } from "./KDateTimeField";
import { UrgencySegments } from "./UrgencySegments";
import { PartPicker } from "./PartPicker";
import { FileTileGrid } from "./FileTileGrid";
import { useKioskFiles } from "./useKioskFiles";

const REPORTS_QUERY = { statusCodes: ["InProgress", "Solved"] };

export function CreateReportV2() {
  const { id } = useParams(); // edit/:id → report NUMBER; create → undefined
  const isNew = !id;
  const [searchParams] = useSearchParams();
  const machineParam = searchParams.get("machine");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { machine, serviceReport } = useAppSelector((s) => s.public);
  const { canWrite } = usePermission(Modules.Machinery);
  const { translate } = useTranslation(["public", "servicing"]);
  const { translate: tCommon } = useTranslation("common");

  // Route-scoped machine (stale plate-A + ?machine=B must refetch B) + scoped report.
  // Shared resolver resolves + backfills the guest's curated payload (no internalNumber),
  // so a guest can actually SUBMIT a fault report (onValid gates on m.internalNumber).
  // The ?machine-absent edit path keeps the original "accept the stored machine" recovery.
  const routedMachine = useKioskRouteMachine(machineParam ?? undefined);
  const m = machineParam ? routedMachine : machine;
  const r = !isNew && serviceReport && id && serviceReport.number?.toLowerCase() === id.toLowerCase() ? serviceReport : undefined;
  const st = useSurfaceState("serviceReport", id); // hook — before any early return

  // Review §4 prefill fix: create-mode openDate defaults to NOW (mount time) so the
  // always-visible date+time fields never render empty; edit mode prefills from the
  // report. The submit-time `|| moment().toDate()` fallback stays as belt-and-braces.
  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<ChangeServiceReport>({
    defaultValues: { title: "", description: "", notes: "", alarmCode: "", urgency: undefined, openDate: isNew ? moment().toDate() : undefined, affectedPartCategoryId: "", files: null },
  });
  const { files, addFiles, removeFile, rejected, dismissRejected, resetFiles } = useKioskFiles(ServiceReportFileTypeCode.reportDocument);

  const [summaryVisible, setSummaryVisible] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const sendPress = usePressed();

  useEffect(() => { if (machineParam && !m) dispatch(getMachine(machineParam)); }, [machineParam, m, dispatch]);
  useEffect(() => { dispatch(getMachineParts()); }, [dispatch]);
  useEffect(() => { if (id) dispatch(getServiceReport(id)); }, [id, dispatch]);

  // Edit-mode Write guard (D4 — mirrors the v1 solve SEC-REQ-3 hotfix). Create stays open to all.
  useEffect(() => {
    if (!isNew && !canWrite) navigate(r?.machineNumber ? `/public-v2/machines/${r.machineNumber}` : "/public-v2", { replace: true });
  }, [isNew, canWrite, r?.machineNumber, navigate]);

  // Edit prefill (v1 verbatim, typed). Create resets to defaults (openDate = now).
  useEffect(() => {
    if (!isNew && r) {
      reset(
        { title: r.title, description: r.description, notes: r.notes || "", alarmCode: r.alarmCode || "", urgency: r.urgency, openDate: r.openDate, affectedPartCategoryId: r.affectedPartCategoryId, files: r.files },
        { keepDefaultValues: true }
      );
      resetFiles(r.files || []);
    } else if (isNew) {
      reset();
      resetFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.number, isNew, reset]);

  useEffect(() => () => { dispatch(setValidationSummary(null)); }, [dispatch]);

  if (!isNew && !canWrite) return null;

  const onInvalid = (fieldErrors: Record<string, unknown>) => {
    setSummaryVisible(true);
    dispatch(setValidationSummary({ count: Object.keys(fieldErrors).length, message: translate("kioskV2ValidationTitle") }));
    summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onValid: SubmitHandler<ChangeServiceReport> = async (model) => {
    if (!m?.internalNumber && isNew) { setSubmitError(translate("machineContextMissing")); return; }
    setSubmitting(true);
    setSubmitError(undefined);
    setSummaryVisible(false);
    dispatch(setValidationSummary(null));
    try {
      model.openDate = model.openDate || moment().toDate();
      model.files = files;
      if (isNew) {
        const res = await dispatch(createServiceReport({ model, machineNumber: m!.internalNumber }));
        if (createServiceReport.rejected.match(res)) { setSubmitError(translate("submitFailed")); return; }
        await dispatch(getServiceReports({ machineNumber: m!.internalNumber, query: REPORTS_QUERY }));
        navigate(`/public-v2/machines/${m!.internalNumber}`);
      } else {
        const res = await dispatch(changeServiceReport({ model, serviceReportNumber: r?.number }));
        if (changeServiceReport.rejected.match(res)) { setSubmitError(translate("submitFailed")); return; }
        const backNumber = r?.machineNumber || m?.internalNumber;
        if (backNumber) await dispatch(getServiceReports({ machineNumber: backNumber, query: REPORTS_QUERY }));
        navigate(`/public-v2/service-reports/${r?.number}`);
      }
    } catch {
      setSubmitError(translate("submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // KV2-5 BUG-1: skeleton covers idle AND pending (via st, hoisted above) — the
  // ErrorCallout only paints when THIS report key actually failed, never on the cold
  // first frame. NO cached-dimmed stale form (§6.4 — save on stale = write-clobber risk).
  const showEditSkeleton = !isNew && !r && !st.failed;
  const showEditError = !isNew && !r && st.failed;

  return (
    <KPage
      titleKey={isNew ? "kioskReportFault" : "editServiceReport"}
      glyph={GD.warn}
      machineNumber={m?.internalNumber || machineParam || r?.machineNumber || undefined}
      backNested={!isNew}
      backTo={!isNew && id ? `/public-v2/service-reports/${id}` : undefined}
    >
      {/* Machine context — kiosk review §4: legible, not thin-grey mono. Name in
          heavy ink + S/N in mono ink-2 (label already relabeled to "S/N" in Pass 1). */}
      {m && (
        <div style={{ padding: "4px 16px 0", display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{getMachineDisplayName(m)}</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)" }}>
            {`${translate("serialNumber")} ${m.serialNumber}`}
          </span>
        </div>
      )}

      {showEditSkeleton ? (
        <FormSkeleton />
      ) : showEditError ? (
        <ErrorCallout
          title={translate("kioskV2ReportLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={() => id && dispatch(getServiceReport(id))}
          retryLabel={translate("kioskV2Retry")}
        />
      ) : (
        <>
          {summaryVisible && (
            <div ref={summaryRef} style={{ margin: "10px 16px 0" }}>
              <ValidationSummary title={translate("kioskV2ValidationTitle")} hint={translate("kioskV2ValidationHint")} />
            </div>
          )}
          <form onSubmit={handleSubmit(onValid, onInvalid)} className="kv2-enter" style={{ padding: "12px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* 1 · Photos (optional — no asterisk, KV2-3-j) */}
            <div>
              <FLabel>{translate("kioskV2PhotosFirst")}</FLabel>
              <FileTileGrid files={files} onAdd={addFiles} onRemove={removeFile} rejected={rejected} onDismissRejected={dismissRejected} variant="photo" inputId="kv2-create-files" />
            </div>

            {/* 2 · Title* — tap-to-edit full-screen editor (§7.2), Controller-driven */}
            <div>
              <FLabel req>{translate("kioskV2WhatsWrong")}</FLabel>
              <Controller control={control} name="title"
                rules={{ required: tCommon("forms.required"), maxLength: { value: 60, message: translate("titleTooLong") } }}
                render={({ field }) => (
                  <EditableField label={translate("kioskV2WhatsWrong")} required singleLine lines={1}
                    value={field.value} onChange={field.onChange} maxLength={60}
                    placeholder={translate("kioskV2TitlePlaceholder")} error={!!errors.title} />
                )} />
              {errors.title && <FieldError>{errors.title.message}</FieldError>}
            </div>

            {/* 3 · Affected part* */}
            <div>
              <FLabel req>{translate("affectedMachinePart")}</FLabel>
              <Controller
                control={control}
                name="affectedPartCategoryId"
                rules={{ required: tCommon("forms.required") }}
                render={({ field }) => <PartPicker value={field.value} onChange={field.onChange} error={!!errors.affectedPartCategoryId} onRetry={() => dispatch(getMachineParts())} />}
              />
              {errors.affectedPartCategoryId && <FieldError>{errors.affectedPartCategoryId.message}</FieldError>}
            </div>

            {/* 4 · Urgency (nullable, PG-14) */}
            <div>
              <FLabel>{translate("urgency")}</FLabel>
              <Controller control={control} name="urgency" render={({ field }) => <UrgencySegments value={field.value} onChange={field.onChange} />} />
            </div>

            {/* 5 · Alarm code */}
            <div>
              <FLabel>{translate("kioskV2AlarmOnScreen")}</FLabel>
              <KTextInput maxLength={100} placeholder={translate("alarmCodePlaceholder")} error={!!errors.alarmCode} {...register("alarmCode", { maxLength: { value: 100, message: translate("alarmCodeTooLong") } })} />
              {errors.alarmCode && <FieldError>{errors.alarmCode.message}</FieldError>}
            </div>

            {/* 6 · Description — tap-to-edit full-screen editor (§7.2) */}
            <div>
              <FLabel>{tCommon("description")}</FLabel>
              <Controller control={control} name="description"
                render={({ field }) => (
                  <EditableField label={tCommon("description")} lines={3}
                    value={field.value} onChange={field.onChange} maxLength={2000}
                    placeholder={translate("kioskV2DescriptionPlaceholder")} />
                )} />
            </div>

            {/* 7 · Open date + time — always visible, prefilled to now (review §4;
                owner override of the D6 disclosure). Single-value merge (bug #7)
                lives in KDateTimeField, unchanged. */}
            <div>
              <Controller
                control={control}
                name="openDate"
                rules={{ validate: (v) => !v || moment(v) <= moment() || translate("openDateFuture") }}
                render={({ field }) => (
                  <KDateTimeField value={field.value} onChange={field.onChange} dateLabel={translate("openDate")} timeLabel={translate("time")} disableFuture error={!!errors.openDate} />
                )}
              />
              {errors.openDate && <FieldError>{errors.openDate.message}</FieldError>}
            </div>

            {/* 8 · Technical notes (D6/PG-13) — tap-to-edit full-screen editor (§7.2) */}
            <div>
              <FLabel>{translate("technicalNotes")}</FLabel>
              <Controller control={control} name="notes"
                render={({ field }) => (
                  <EditableField label={translate("technicalNotes")} lines={3}
                    value={field.value} onChange={field.onChange} maxLength={4000}
                    placeholder={translate("technicalNotesPlaceholder")} />
                )} />
            </div>

            {/* 9 · Submit error */}
            {submitError && <ErrorCallout title={submitError} />}

            {/* 10 · Send (disabled ONLY while submitting — send-stays-enabled) */}
            <div style={{ padding: "16px 0 0" }}>
              <button
                type="submit"
                {...sendPress.pressHandlers}
                disabled={submitting}
                style={{ width: "100%", height: 56, borderRadius: 13, border: "none", background: "var(--accent)", color: "var(--tier-accent-fg)", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: submitting ? 0.6 : 1, ...sendPress.pressStyle }}
              >
                {isNew ? translate("kioskV2SendReport") : tCommon("buttons.save")}
              </button>
            </div>
          </form>
        </>
      )}
    </KPage>
  );
}

export default CreateReportV2;
